/**
 * DTOs de request/response dos endpoints da SPEC §3.
 *
 * Cada contrato tem um schema Zod (validação em runtime na borda da API)
 * e o tipo TS inferido. A API é a fonte da verdade do contrato externo;
 * o schema Prisma é detalhe de persistência.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Enums como schemas Zod (espelham packages/shared/src/enums.ts).
// ---------------------------------------------------------------------------

export const zCeremonyMode = z.enum([
  'unitary',
  'sequential',
  'parallel_quorum',
  'batch_async',
]);
export const zProfileTarget = z.enum(['AD-RB', 'AD-RT', 'AD-RV', 'AD-RC', 'AD-RA']);
export const zSignatureLevel = z.enum(['simples', 'avancada', 'qualificada']);
export const zDocumentFormat = z.enum(['pades', 'cades', 'xades']);
export const zKeyProvider = z.enum(['local_agent', 'psc_partner']);
export const zParticipantRole = z.enum(['signer', 'observer']);
export const zEnvelopeStatus = z.enum([
  'draft',
  'ready',
  'in_progress',
  'completed',
  'declined',
  'expired',
  'cancelled',
]);
export const zSignerStatus = z.enum([
  'pending',
  'notified',
  'in_session',
  'signed',
  'declined',
  'expired',
]);
export const zConsumptionKind = z.enum(['signature', 'timestamp', 'validation']);

// ---------------------------------------------------------------------------
// POST /v1/envelopes — cria envelope em draft.
// ---------------------------------------------------------------------------

export const zDocumentInput = z
  .object({
    /** Nome/identificação opcional do documento. */
    name: z.string().min(1).max(255).optional(),
    format: zDocumentFormat,
    /**
     * SHA-256 do documento original (hex de 64 chars). Opcional quando `content_b64`
     * é enviado — nesse caso a API deriva o hash dos bytes e, se ambos vierem, exige
     * que batam (âncora de integridade — D-009).
     */
    hash_sha256: z
      .string()
      .regex(/^[0-9a-f]{64}$/i, 'hash_sha256 deve ser SHA-256 em hex (64 chars)')
      .optional(),
    /**
     * Bytes do documento em Base64 (custódia transitória — D-009). O motor de formato
     * precisa dos BYTES (PAdES insere a assinatura dentro do PDF), não só do hash. Os
     * bytes vão para o object store e são descartados após a entrega.
     */
    content_b64: z.string().base64('content_b64 deve ser Base64 válido').optional(),
  })
  .refine((d) => Boolean(d.content_b64) || Boolean(d.hash_sha256), {
    message: 'informe content_b64 (bytes do documento) ou hash_sha256',
  });
export type DocumentInput = z.infer<typeof zDocumentInput>;

export const zCreateEnvelopeRequest = z.object({
  documents: z.array(zDocumentInput).min(1, 'informe ao menos 1 documento'),
  ceremony_mode: zCeremonyMode,
  profile_target: zProfileTarget,
  signature_level: zSignatureLevel,
  /** Para parallel_quorum: M de N. Ignorado nos demais modos. */
  quorum: z.number().int().positive().optional(),
  /** ISO-8601. Se ausente, a API aplica o default do plano. */
  expires_at: z.string().datetime().optional(),
});
export type CreateEnvelopeRequest = z.infer<typeof zCreateEnvelopeRequest>;

// ---------------------------------------------------------------------------
// Posicionamento do carimbo visível de assinatura (aparência da assinatura,
// D-027 — Frente Node). Aditivo: opcional em zAddSignerRequest, ausente por
// default (comportamento idêntico byte a byte quando não informado — a regra de
// negócio que monta o TEXTO do carimbo vive em domain/visual-stamp.ts, não aqui).
//
// `page` 1-based; `x`/`y` são o canto SUPERIOR-ESQUERDO do carimbo, fração 0..1
// da página (origem topo-esquerda, mesmo espaço de coordenadas do
// `EngineVisual` que o format-engine consome — ver clients/format-engine.ts).
// `x+width` e `y+height` não podem ultrapassar 1 (carimbo tem que caber na
// página).
//
// LIMITES DE TAMANHO (M5a). A regra `width ∈ [0.1,0.6]` / `height ∈ [0.04,0.2]`
// já existia — mas só dentro do `signing-workbench` (`ui/lib/stamp-geometry.ts` e
// `server/ceremony/visual.ts`), enquanto ESTE contrato, que é o que a `/v1` e a
// `/web` realmente aplicam, aceitava qualquer fração de (0,1]. Duas regras para a
// mesma coisa é como nasce divergência calada (mesmo motivo da D-038: um schema
// só, importado, nunca redefinido). Os limites sobem para cá e passam a valer
// igualmente para quem monta o envelope (owner) e para quem assina (signatário,
// que arrasta o carimbo na cerimônia). Piso: abaixo de 10% da largura o carimbo
// fica ilegível no impresso. Teto: acima de 60%×20% deixa de ser carimbo e vira
// tarja sobre o texto do documento.
// ---------------------------------------------------------------------------

/** Faixa de tamanho do carimbo, em fração da página. Ver bloco acima. */
export const STAMP_WIDTH_MIN = 0.1;
export const STAMP_WIDTH_MAX = 0.6;
export const STAMP_HEIGHT_MIN = 0.04;
export const STAMP_HEIGHT_MAX = 0.2;

export const zVisualPlacement = z
  .object({
    page: z.number().int().min(1).default(1),
    x: z.number().gte(0).lt(1),
    y: z.number().gte(0).lt(1),
    width: z.number().gte(STAMP_WIDTH_MIN).lte(STAMP_WIDTH_MAX),
    height: z.number().gte(STAMP_HEIGHT_MIN).lte(STAMP_HEIGHT_MAX),
  })
  .refine((v) => v.x + v.width <= 1, {
    message: 'x + width não pode ultrapassar 1 (carimbo sairia da página)',
    path: ['width'],
  })
  .refine((v) => v.y + v.height <= 1, {
    message: 'y + height não pode ultrapassar 1 (carimbo sairia da página)',
    path: ['height'],
  });
export type VisualPlacement = z.infer<typeof zVisualPlacement>;

// ---------------------------------------------------------------------------
// POST /v1/envelopes/:id/signers — adiciona signatário (só em draft).
// ---------------------------------------------------------------------------

export const zAddSignerRequest = z
  .object({
    name: z.string().min(1).max(255),
    role: zParticipantRole.default('signer'),
    cpf: z.string().regex(/^\d{11}$/, 'cpf deve ter 11 dígitos (só números)').optional(),
    email: z.string().email(),
    phone: z.string().min(8).max(20).optional(),
    /** Ordem estável de apresentação. Se ausente, vai para o fim da fila. */
    order: z.number().int().nonnegative().optional(),
    /** Modalidade exigida deste signer. Nunca pode ser rebaixada automaticamente. */
    signature_level: zSignatureLevel.optional(),
    /** Stage 1-based de disponibilidade. */
    stage: z.number().int().min(1).optional(),
    key_provider: zKeyProvider,
    /** Obrigatório quando key_provider = psc_partner. */
    psc_driver_id: z.string().min(1).optional(),
    /** Política de autenticação por tenant (email_otp, sms_otp, selfie_biometria...). */
    auth_required: z.array(z.string()).default([]),
    /**
     * Carimbo visível deste signatário no documento (opcional). Só aceito quando o
     * documento do envelope é `pades` (a rota valida e recusa com 400 explícito
     * caso contrário — cades/xades não têm carimbo visual).
     */
    visual: zVisualPlacement.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.role === 'signer' && value.cpf === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['cpf'], message: 'cpf é obrigatório para signer' });
    }
    if (value.role === 'observer') {
      for (const field of ['signature_level', 'stage', 'visual'] as const) {
        if (value[field] !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: `${field} não é aceito para observer`,
          });
        }
      }
    }
  });
export type AddSignerRequest = z.infer<typeof zAddSignerRequest>;

// ---------------------------------------------------------------------------
// POST /v1/signers/:id/session — abre a sessão de assinatura (D-009).
// A cadeia de certs entra AQUI (não mais no complete): CAdES/PAdES amarram o
// certificado do signatário aos atributos assinados, então o motor precisa dela
// no `init` para calcular o DTBS.
//
// EXCEÇÃO — nível "simples" (selo do servidor, Lei 14.063/2020, D-008/WS3): é uma
// sessão de CONSENTIMENTO, sem cadeia de certs do signatário (quem sela é a chave
// da PLATAFORMA). `cert_chain` é opcional aqui só por isso; a rota (signers.ts)
// valida com 400 explícito quando o nível exige agente local e ele não veio.
// ---------------------------------------------------------------------------

export const zOpenSessionRequest = z.object({
  /**
   * Cadeia de certificados públicos [end-entity, intermediárias...] em DER Base64,
   * enviada pelo agente local. A chave privada nunca trafega (RF-06). Obrigatória
   * para nível avancada/qualificada; ausente/ignorada no nível simples (D-008).
   */
  cert_chain: z
    .array(z.string().min(1))
    .min(1, 'informe ao menos o certificado do titular (end-entity)')
    .optional(),
});
export type OpenSessionRequest = z.infer<typeof zOpenSessionRequest>;

// ---------------------------------------------------------------------------
// POST /v1/signers/:id/session/complete — recebe assinatura do agente/PSC.
// A cadeia de certs NÃO vem aqui (foi na abertura da sessão — D-009); o complete
// só correlaciona pela sessão do motor e recebe o valor assinado + algoritmo.
//
// EXCEÇÃO — nível "simples": não há assinatura calculada externamente (quem sela
// é a chave da plataforma, dentro do format-engine); `signed_value` é opcional só
// por isso — a rota valida com 400 explícito quando o nível exige agente local.
// ---------------------------------------------------------------------------

export const zCompleteSessionRequest = z.object({
  /**
   * Valor da assinatura (bruto) devolvido pelo agente local ou callback do PSC.
   * Obrigatório para nível avancada/qualificada; ausente/ignorado no nível simples.
   */
  signed_value: z.string().min(1).optional(),
  /** Algoritmo da assinatura (ex.: RSA_SHA256). Default MVP espelha o motor de formato. */
  algorithm: z.string().min(1).default('RSA_SHA256'),
});
export type CompleteSessionRequest = z.infer<typeof zCompleteSessionRequest>;

// ---------------------------------------------------------------------------
// POST /v1/signers/:id/decline — registra recusa com motivo.
// ---------------------------------------------------------------------------

export const zDeclineRequest = z.object({
  reason: z.string().min(1).max(1000),
});
export type DeclineRequest = z.infer<typeof zDeclineRequest>;

// ---------------------------------------------------------------------------
// Responses.
// ---------------------------------------------------------------------------

export interface DocumentView {
  id: string;
  name: string | null;
  format: z.infer<typeof zDocumentFormat>;
  hash_sha256: string;
}

export interface SignerView {
  id: string;
  order: number;
  role: z.infer<typeof zParticipantRole>;
  required_signature_level: z.infer<typeof zSignatureLevel>;
  actual_signature_level: z.infer<typeof zSignatureLevel> | null;
  stage: number;
  name: string;
  /** `null` após a eliminação de PII sob demanda (LGPD Art. 18, D-029): o titular foi
   *  anonimizado in-place (name vira o sentinela "[titular removido]", cpf/email/phone viram
   *  null). Em qualquer outro caso é o e-mail informado no add-signer. */
  email: string | null;
  status: z.infer<typeof zSignerStatus>;
  /**
   * `null` na leitura MINIMIZADA por um co-participante (U1-004/U1-002): quando um SIGNATÁRIO lê
   * `GET /v1/envelopes/:id` via SST, o `key_provider` dos OUTROS participantes é redigido a `null`
   * (nenhum consumidor o lê da SignerView; a trilha do signer-ui não o usa). Para o próprio
   * signatário e para API key/owner é sempre o provedor real informado no add-signer.
   */
  key_provider: z.infer<typeof zKeyProvider> | null;
  signed_at: string | null;
  decline_reason: string | null;
  /** Aditivo: carimbo visível configurado para este signatário; `null` quando ausente. */
  visual: VisualPlacement | null;
  /**
   * Versão de `visual` (M5a). Existe para o cliente poder mandar `If-Match` no
   * `PUT /v1/signers/:id/visual` desde a PRIMEIRA gravação — sem ela, o primeiro gesto ia sem
   * precondição e um retry de rede podia sobrescrever uma escolha mais nova sem erro nenhum.
   * Começa em 0 e incrementa a cada gravação do signatário.
   */
  visual_version: number;
  /**
   * Aditivo (D-063 item 1, retomada de estado): carimbo da verificação do CANAL — hoje só a
   * Simples tem canal (OTP por e-mail), então `null` em avancada/qualificada é o estado normal,
   * não "pendente". Espelha `Signer.channelVerifiedAt`.
   */
  channel_verified_at: string | null;
  /**
   * Aditivo (D-063 item 1/3, retomada de estado): carimbo de aceite do consentimento VÁLIDO para
   * o documento corrente na versão corrente dos termos (`CONSENT_TERMS_VERSION`) — exatamente o
   * registro que `POST /v1/signers/:id/session/complete` exige de TODO signatário (D-063 item 3).
   * `null` = não há esse registro, em qualquer modalidade. A cerimônia recarregada retoma do
   * ponto certo comparando estes dois campos.
   */
  consent_given_at: string | null;
}

/**
 * GET /v1/signers/:id/certificate-expectation (D-063 item 2 — filtro de certificados por CPF).
 *
 * O CPF esperado do participante NUNCA sai do servidor. A tela da Qualificada extrai o CPF do
 * titular da folha do certificado (CN "NOME:CPF", dígitos após o ÚLTIMO ':' — mesma convenção de
 * `extractSubjectCpf`), calcula `sha256hex(envelope_id + ':' + cpf)` e compara com `cpf_match`:
 * casou, o certificado é do titular convidado; não casou, nunca aparece na lista.
 *
 * `masked_cpf` é do PRÓPRIO signatário autenticado (nunca de terceiro) e existe só para a
 * mensagem de estado vazio ("nenhum certificado de ***.456.789-** encontrado").
 *
 * Ambos `null` quando não há expectativa a comparar: signatário sem CPF cadastrado, CPF eliminado
 * (LGPD Art. 18) ou modalidade que não usa certificado (simples). Isto NÃO é autorização — a
 * autoridade do vínculo certificado ↔ signatário continua sendo `checkCertificateBinding`
 * (D-035), server-side, na abertura da sessão.
 */
export interface SignerCertificateExpectationView {
  cpf_match: string | null;
  masked_cpf: string | null;
}

/** GET /v1/envelopes/:id — status completo do envelope e signatários. */
export interface EnvelopeView {
  id: string;
  tenant_id: string;
  status: z.infer<typeof zEnvelopeStatus>;
  ceremony_mode: z.infer<typeof zCeremonyMode>;
  profile_target: z.infer<typeof zProfileTarget>;
  signature_level: z.infer<typeof zSignatureLevel>;
  quorum: number | null;
  /** Lock otimista — repasse no header/If-Match para transições concorrentes. */
  version: number;
  created_at: string;
  expires_at: string | null;
  documents: DocumentView[];
  signers: SignerView[];
}

/** POST /v1/signers/:id/session — devolve o DTBS para o agente/PSC (D-009). */
export interface SessionView {
  id: string;
  signer_id: string;
  /**
   * Data-to-be-signed (Base64) que o agente/PSC de fato assina. Para CAdES/PAdES é o
   * bloco de atributos assinados (ToBeSigned do DSS), amarrado ao cert enviado na abertura.
   */
  dtbs_hash: string;
  nonce: string;
  /**
   * Estado da SigningSession. `completing` (débito #19, hardening-base Seam A) é o lock de
   * conclusão em voo: existe entre o início de `POST .../session/complete` e a persistência do
   * resultado. Enquanto a sessão está nesse estado, um novo `complete` recebe
   * `409 completion_in_progress` (com `Retry-After`) em vez de re-executar a assinatura.
   */
  status:
    | z.infer<typeof zSignerStatus>
    | 'created'
    | 'delivered'
    | 'completing'
    | 'signed'
    | 'expired';
  expires_at: string;
}

/**
 * POST /v1/signers/:id/session/complete — resposta após concluir a assinatura.
 * `signed_document` é aditivo (D-009): aponta para o endpoint de download do
 * artefato assinado, que fica em custódia transitória até o tenant baixar.
 */
export interface CompleteSessionResponse {
  signer: SignerView;
  envelope_completed: boolean;
  quota: { count: number; limit: number | null; alert: string };
  signed_document: {
    document_id: string;
    /** Caminho relativo (mesmo host/tenant) do GET que devolve os bytes assinados. */
    download_path: string;
  };
}

/** GET /v1/tenants/:id/consumption — consumo em tempo real (cache quente Redis). */
export interface ConsumptionView {
  tenant_id: string;
  period: string;
  usage: Record<z.infer<typeof zConsumptionKind>, { used: number; limit: number | null }>;
  quota_policy: 'block' | 'soft' | 'overage';
  /** Origem do número: 'redis' (quente) ou 'durable' (fallback Postgres). */
  source: 'redis' | 'durable';
}

/** Corpo padrão de erro da API. */
export interface ApiErrorBody {
  error: string;
  message: string;
  /** Presente em 409 de transição inválida / lock otimista. */
  current_state?: string;
  expected_version?: number;
  /**
   * D-034 (débito #14, Rodada de conformidade — fail-closed, sem conversão silenciosa de
   * modalidade): presente em `422 certificate_expired` (ISO-8601 UTC) — data/hora em que o
   * certificado expirou, para a mensagem de UX ("Certificado vencido — expirou em ..."). Aditivo
   * e vindo do motor (`FormatEngineError.engineDetails.not_after`, ver clients/format-engine.ts);
   * pode estar ausente se o motor não informou o detalhe.
   */
  not_after?: string;
  /**
   * D-034 (débito #14): presente em `422 certificate_not_yet_valid` (ISO-8601 UTC) — data/hora a
   * partir da qual o certificado passa a valer (notBefore no futuro). Mesma origem/aditividade
   * de `not_after` acima.
   */
  not_before?: string;
  /**
   * D-034 (débito #17 — validar o artefato ANTES de concluir): presente em
   * `500 artifact_validation_failed` (POST /v1/signers/:id/session/complete). Usa o MESMO
   * vocabulário tri-state de `POST /v1/validate` (routes/validate.ts) para os estados
   * NÃO-aprovados — `'invalid'` (assinatura reprovada) ou `'indeterminate'` (sem dado suficiente
   * para aprovar; só conclui com `ARGINK_REVOCATION_MODE=warn`). Nunca `'valid'` neste corpo — um
   * artefato aprovado não gera este erro. Tipado como `string` (não union literal) de propósito:
   * `apps/signer-ui/src/lib/api.ts` já declara `ApiErrorBodyWire extends ApiErrorBody` com este
   * campo solto (seam paralela da mesma D-034, fora do escopo deste seam tocar) — um literal
   * union aqui quebraria aquele `extends`. O vocabulário exato continua sendo só os dois valores
   * acima; ver routes/signers.ts/routes/validate.ts para quem escreve o campo de verdade.
   */
  validation_status?: string;
}
